import { useState, useEffect } from 'react';
import { MultiColumnMatchingRule, ColumnInputState } from './types';
import {
  createUniquePasteHandler,
  PasteResult,
} from '../../../utils/excelUtils';

export const useMultiColumnMatching = (
  open: boolean,
  initialRules: MultiColumnMatchingRule[] = [],
  fieldName?: string
) => {
  const [rules, setRules] = useState<MultiColumnMatchingRule[]>([]);
  const [inputValues, setInputValues] = useState<{
    [key: string]: ColumnInputState;
  }>({});

  useEffect(() => {
    if (open) {
      // Initialize with default rules for Deposit/Withdraw and merge with any existing rules
      if (fieldName === 'Deposit/Withdraw') {
        const defaults: MultiColumnMatchingRule[] = [
          {
            standardValue: 'Deposit',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Withdraw',
            column1Values: [],
            column2Values: [],
          },
        ];

        const byStandard: Record<string, MultiColumnMatchingRule> = {};
        (initialRules || []).forEach(r => {
          if (r && r.standardValue) {
            byStandard[r.standardValue] = {
              standardValue: r.standardValue,
              column1Values: r.column1Values || [],
              column2Values: r.column2Values || [],
            };
          }
        });

        const merged = defaults.map(d => ({
          standardValue: d.standardValue,
          column1Values: byStandard[d.standardValue]?.column1Values || [],
          column2Values: byStandard[d.standardValue]?.column2Values || [],
        }));

        setRules(merged);
      } else if (fieldName === 'Fee Type') {
        const defaults: MultiColumnMatchingRule[] = [
          {
            standardValue: 'Custodian Fee',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Income Adjustment',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Interest',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Management Fee',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Misc Charge',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'P&L Adjustment',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Performance Fee',
            column1Values: [],
            column2Values: [],
          },
          {
            standardValue: 'Withholding Tax',
            column1Values: [],
            column2Values: [],
          },
        ];

        const byStandard: Record<string, MultiColumnMatchingRule> = {};
        (initialRules || []).forEach(r => {
          if (r && r.standardValue) {
            byStandard[r.standardValue] = {
              standardValue: r.standardValue,
              column1Values: r.column1Values || [],
              column2Values: r.column2Values || [],
            };
          }
        });

        const merged = defaults.map(d => ({
          standardValue: d.standardValue,
          column1Values: byStandard[d.standardValue]?.column1Values || [],
          column2Values: byStandard[d.standardValue]?.column2Values || [],
        }));

        setRules(merged);
      } else {
        // Ensure all rules have the required arrays initialized
        const sanitizedRules = (initialRules || []).map(rule => ({
          standardValue: rule.standardValue || '',
          column1Values: rule.column1Values || [],
          column2Values: rule.column2Values || [],
        }));
        setRules(sanitizedRules.length > 0 ? sanitizedRules : []);
      }
      // Clear input values when modal opens
      setInputValues({});
    }
  }, [open, initialRules, fieldName]);

  const getInputValue = (
    standardValue: string,
    column: 'column1' | 'column2'
  ): string => {
    const state = inputValues[standardValue] || { column1: '', column2: '' };
    return state[column] || '';
  };

  const setInputValue = (
    standardValue: string,
    column: 'column1' | 'column2',
    value: string
  ) => {
    setInputValues(prev => ({
      ...prev,
      [standardValue]: {
        column1: prev[standardValue]?.column1 || '',
        column2: prev[standardValue]?.column2 || '',
        [column]: value,
      },
    }));
  };

  const addValueToColumn = (
    ruleIndex: number,
    column: 'column1' | 'column2',
    value: string
  ): boolean => {
    if (!value.trim()) return false;

    const newRules = [...rules];
    const trimmedValue = value.trim();
    const columnKey = column === 'column1' ? 'column1Values' : 'column2Values';

    if (!newRules[ruleIndex][columnKey].includes(trimmedValue)) {
      newRules[ruleIndex][columnKey].push(trimmedValue);
      setRules(newRules);

      // Clear the input value for this specific column after adding
      const standardValue = newRules[ruleIndex].standardValue;
      setInputValues(prev => ({
        ...prev,
        [standardValue]: {
          column1: prev[standardValue]?.column1 || '',
          column2: prev[standardValue]?.column2 || '',
          [column]: '',
        },
      }));
      return true;
    }
    return false;
  };

  const addMultipleValuesToColumn = (
    ruleIndex: number,
    column: 'column1' | 'column2',
    values: string[]
  ): number => {
    if (!values.length) return 0;

    const newRules = [...rules];
    const columnKey = column === 'column1' ? 'column1Values' : 'column2Values';
    const validValues = values
      .map(value => value.trim())
      .filter(
        value => value && !newRules[ruleIndex][columnKey].includes(value)
      );

    if (validValues.length > 0) {
      newRules[ruleIndex][columnKey].push(...validValues);
      setRules(newRules);

      // Clear the input value after adding
      const standardValue = newRules[ruleIndex].standardValue;
      setInputValues(prev => ({
        ...prev,
        [standardValue]: {
          column1: prev[standardValue]?.column1 || '',
          column2: prev[standardValue]?.column2 || '',
          [column]: '',
        },
      }));
    }

    return validValues.length;
  };

  const handlePaste = (
    e: React.ClipboardEvent,
    ruleIndex: number,
    column: 'column1' | 'column2'
  ): PasteResult => {
    const pasteHandler = createUniquePasteHandler(
      (value: string) => addValueToColumn(ruleIndex, column, value),
      (values: string[]) => addMultipleValuesToColumn(ruleIndex, column, values)
    );

    return pasteHandler(e);
  };

  const removeValueFromColumn = (
    ruleIndex: number,
    column: 'column1' | 'column2',
    valueIndex: number
  ) => {
    setRules(prevRules => {
      const newRules = prevRules.map((rule, index) => {
        if (index === ruleIndex) {
          const columnKey =
            column === 'column1' ? 'column1Values' : 'column2Values';
          const updatedValues = rule[columnKey].filter(
            (_, i) => i !== valueIndex
          );
          return {
            ...rule,
            [columnKey]: updatedValues,
          };
        }
        return rule;
      });
      return newRules;
    });
  };

  const handleKeyPress = (
    e: React.KeyboardEvent,
    ruleIndex: number,
    column: 'column1' | 'column2'
  ) => {
    const standardValue = rules[ruleIndex].standardValue;
    const value = getInputValue(standardValue, column);
    if (e.key === 'Enter' && value.trim()) {
      addValueToColumn(ruleIndex, column, value);
    }
  };

  const getValidRules = (): MultiColumnMatchingRule[] => {
    // Keep rules with a standardValue even if one is empty (prevents Deposit from disappearing)
    const validRules = rules
      .filter(rule => rule.standardValue.trim())
      .map(rule => ({
        standardValue: rule.standardValue,
        column1Values: (rule.column1Values || []).filter(v => v && v.trim()),
        column2Values: (rule.column2Values || []).filter(v => v && v.trim()),
      }));
    return validRules;
  };

  const hasValidRules = rules.some(
    rule =>
      rule.standardValue.trim() &&
      (rule.column1Values.length > 0 || rule.column2Values.length > 0)
  );

  return {
    rules,
    getInputValue,
    setInputValue,
    addValueToColumn,
    addMultipleValuesToColumn,
    removeValueFromColumn,
    handleKeyPress,
    handlePaste,
    getValidRules,
    hasValidRules,
  };
};
